import React from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { authStorage } from "../../utils/authStorage";
// MainLayout must be a function to use hooks

const MainLayout = ({ children }) => {
    const [brandSnapshot, setBrandSnapshot] = React.useState(null);
    // Sidebar open state: open by default on large screens, closed on md and below
    const [sidebarOpen, setSidebarOpen] = React.useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth >= 1024; // Tailwind's lg breakpoint
        }
        return false;
    });
    const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
        const storedUser = authStorage.getUser();
        return storedUser?.user_type === 2 || storedUser?.user_type === 3;
    });

    React.useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024) {
                setSidebarOpen(true);
            } else {
                setSidebarOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleSidebar = () => {
        if (typeof window !== "undefined" && window.innerWidth >= 1024) {
            setSidebarCollapsed((prev) => !prev);
            setSidebarOpen(true);
            return;
        }
        setSidebarOpen((prev) => !prev);
    };
    const closeSidebar = () => {
        if (typeof window !== "undefined" && window.innerWidth >= 1024) {
            setSidebarOpen(true);
            return;
        }
        setSidebarOpen(false);
    };
    const isDesktop = typeof window !== "undefined" ? window.innerWidth >= 1024 : false;
    const effectiveSidebarCollapsed = isDesktop ? sidebarCollapsed : false;

    return (
        <div className="h-screen bg-gray-50 overflow-hidden flex flex-col">
            {/* Header spans full width at top */}
            <Header
                toggleSidebar={toggleSidebar}
                brandSnapshot={brandSnapshot}
                sidebarCollapsed={sidebarCollapsed}
            />

            {/* Overlay for mobile/tablet when sidebar is open */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 lg:hidden"
                    onClick={closeSidebar}
                    aria-label="Close sidebar overlay"
                />
            )}

            {/* Burger menu icon handled by Header, do not render duplicate here */}

            {/* Below header: sidebar and main content side by side */}
            <div className="relative flex flex-1 min-h-0">
                <div
                    className={`relative flex-none transition-all duration-300 ease-in-out ${
                        sidebarOpen ? 'block fixed inset-y-0 left-0 z-50' : 'hidden'
                    } ${effectiveSidebarCollapsed ? 'lg:w-24' : 'lg:w-64'} lg:static lg:block lg:h-auto lg:z-auto`}
                    style={isDesktop ? undefined : { width: "16rem" }}
                >
                    {/* Sidebar (fixed width, scrollable if needed) */}
                    <aside
                        className="relative h-full overflow-visible rounded-r-2xl bg-white shadow-2xl lg:rounded-none lg:shadow"
                    >
                        <div className="h-full overflow-y-auto">
                        <Sidebar
                            isOpen={sidebarOpen}
                            closeSidebar={closeSidebar}
                            onBrandUpdated={setBrandSnapshot}
                            collapsed={effectiveSidebarCollapsed}
                        />
                        </div>

                        {isDesktop && (
                            <button
                                type="button"
                                onClick={toggleSidebar}
                                className="absolute top-3 right-0 z-30 hidden h-9 w-9 translate-x-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-[0_10px_24px_rgba(15,23,42,0.12)] transition hover:bg-slate-50 lg:inline-flex"
                                aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                                title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                            >
                                {sidebarCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
                            </button>
                        )}
                    </aside>
                </div>

                {/* Main content area - compact padding for all */}
                <main className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray-50 px-2 py-3 sm:px-5 sm:py-4">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default MainLayout;
